import { alpha, Box, ButtonBase, InputBase, styled } from '@mui/material';

export const SearchBarRow = styled(Box, { name: 'SearchPaneBarRow' })({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  margin: '0 0.25rem 0.5rem',
  flexShrink: 0,
});

export const SearchField = styled(Box, { name: 'SearchPaneField' })(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: '0.5rem',
  padding: '0.375rem 0.5rem 0.375rem 0.625rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,

  '&:focus-within': {
    borderColor: theme.palette.custom.OV.sys.outline,
  },
}));

export const SearchInput = styled(InputBase, { name: 'SearchPaneInput' })(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '0.78rem',
  color: theme.palette.custom.OV.sys.onBackground,

  '& .MuiInputBase-input': {
    padding: 0,

    '&::placeholder': {
      color: theme.palette.custom.OV.sys.onSurfaceVariant,
      opacity: 1,
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-input': {
      fontSize: theme.utils?.important?.('1rem') ?? '1rem !important',
    },
  },
}));

// hover mirrors the nav grouping buttons (NavGroupTab): surfaceContainerHighest
export const ExpandButton = styled(ButtonBase, { name: 'SearchPaneExpandButton' })(
  ({ theme }) => ({
    width: '1.875rem',
    height: '1.875rem',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '0.5rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    transition: 'background-color .12s, color .12s',

    '&:hover': {
      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
      color: theme.palette.custom.OV.sys.onBackground,
    },
  }),
);

export const ClearButton = styled(ButtonBase, { name: 'SearchPaneClearButton' })(
  ({ theme }) => ({
    width: '1.125rem',
    height: '1.125rem',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '999px',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    transition: 'background-color .12s, color .12s',

    '&:hover': {
      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
      color: theme.palette.custom.OV.sys.onBackground,
    },
  }),
);

export const FilterRow = styled(Box, { name: 'SearchPaneFilterRow' })({
  display: 'flex',
  gap: '0.25rem',
  overflowX: 'auto',
  padding: '0 0.25rem 0.5rem',
  flexShrink: 0,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

export const FilterChip = styled(ButtonBase, {
  name: 'SearchPaneFilterChip',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  fontFamily: 'inherit',
  fontSize: '0.66rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
  padding: '0.1875rem 0.5625rem',
  borderRadius: '999px',
  border: `1px solid ${
    active
      ? theme.palette.custom.OV.sys.secondaryContainer
      : theme.palette.custom.OV.sys.outlineVariant
  }`,
  backgroundColor: active ? theme.palette.custom.OV.sys.secondaryContainer : 'transparent',
  color: active
    ? theme.palette.custom.OV.sys.onSecondaryContainer
    : theme.palette.custom.OV.sys.onSurfaceVariant,

  '&:hover': {
    color: active
      ? theme.palette.custom.OV.sys.onSecondaryContainer
      : theme.palette.custom.OV.sys.onBackground,
    borderColor: active
      ? theme.palette.custom.OV.sys.secondaryContainer
      : theme.palette.custom.OV.sys.outline,
  },
}));

export const ResultsList = styled(Box, { name: 'SearchPaneResultsList' })({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '0.25rem 0 1rem',
  overscrollBehavior: 'contain',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

export const ResultRow = styled(ButtonBase, {
  name: 'SearchPaneResultRow',
  shouldForwardProp: (prop) => prop !== 'alignCenter',
})<{ alignCenter?: boolean }>(({ theme, alignCenter }) => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1.5rem 1fr',
  gap: '0.625rem',
  // single-line results (e.g. a client with no email) center the icon/avatar
  // against the title; multi-line results top-align
  alignItems: alignCenter ? 'center' : 'flex-start',
  justifyItems: 'start',
  padding: '0.375rem 0.5rem',
  borderRadius: '0.4375rem',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,

  // keyboard focus reuses the hover background (no bright outline)
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  },
}));

export const ResultMeta = styled(Box, { name: 'SearchPaneResultMeta' })(({ theme }) => ({
  minWidth: 0,
  width: '100%',

  '& mark': {
    backgroundColor: theme.palette.custom.OV.extended.Signature.colorContainer,
    color: theme.palette.custom.OV.extended.Signature.color,
    padding: '0 0.125rem',
    borderRadius: '0.125rem',
  },
}));

export const ResultTitle = styled(Box, { name: 'SearchPaneResultTitle' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: theme.palette.custom.OV.sys.onBackground,
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '& > span:first-of-type': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

// tinted from the conversation status color (open/solved/snoozed…) when known,
// falling back to a neutral chip; mirrors the mockup's per-status badge accent
export const ResultBadge = styled('span', {
  name: 'SearchPaneResultBadge',
  shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent?: string }>(({ theme, accent }) => ({
  flexShrink: 0,
  fontSize: '0.59rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '0.0625rem 0.375rem',
  borderRadius: '0.25rem',
  backgroundColor: accent ? alpha(accent, 0.12) : theme.palette.custom.OV.sys.surfaceContainer,
  color: accent ?? theme.palette.custom.OV.sys.onSurfaceVariant,
  border: `1px solid ${accent ? alpha(accent, 0.3) : theme.palette.custom.OV.sys.outlineVariant}`,
}));

export const ResultSubtitle = styled(Box, { name: 'SearchPaneResultSubtitle' })(
  ({ theme }) => ({
    fontSize: '0.6875rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    marginTop: '0.0625rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

export const EmptyState = styled(Box, { name: 'SearchPaneEmptyState' })(({ theme }) => ({
  padding: '1.5rem 0.75rem',
  textAlign: 'center',
  fontSize: '0.75rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));
